import './rf-tab.sass';

import React from 'react';
import {connect} from 'react-redux';
import * as classnames from 'classnames';

import {currentServerUnixTime, prettyTimeInterval} from '../../../../utils/time';
import {LegacyDevice} from '../../../device/device';

import {
    RFBatteryIndicator,
    RFSignalIndicator,
} from '../../../react-elements/rf-indicators';
import {human_serial, rf_title_by_type, show_temp_for_rf_type} from './rf-utils';
import {TimedRenderer} from '../../../react-elements/timed-renderer';
import {reloadDeviceRF} from '../../../redux/devices/items/actions';
import {selectSelectedDevice} from '../../../entities/device/lib';
import {formatTemp} from '../../../../utils';
import {Capability} from '../../../device/device-capability';

const RFTempIndicator = ({temp}) => (
    <div className="ui-rf-tab-temp" title="Температура">
        <span className="ui-rf-tab-temp-icon" />
        {formatTemp(temp)}
    </div>
);

const RFWaterIndicator = ({water}) => {
    let icon, title;
    if (water === true) [icon, title] = ['wet', 'Обнаружена протечка!'];
    if (water === false) [icon, title] = ['dry', 'Протечки нет'];
    if (water === 'break') [icon, title] = ['wet', 'Обрыв датчика!'];

    return <div className={classnames('ui-rf-tab-water-icon', icon)} title={title} />;
};

const RFMotionIndicator = ({motion}) => {
    let icon, title;
    if (motion) [icon, title] = ['motion', 'Обнаружено движение!'];
    else [icon, title] = ['no-motion', 'Движения нет'];

    return <div className={classnames('ui-rf-tab-motion', icon)} title={title} />;
};

const RFHumidityIndicator = ({hum}) => (
    <div className="ui-rf-tab-humidity" title="Влажность">
        <span className="ui-rf-tab-humidity-icon" />
        {hum}
        <span className="ui-rf-tab-percent">%</span>
    </div>
);

export const RFLastDataTime = ({current_time, time, type}) => {
    const threshold = 30 * 60;
    const diff = current_time - time;

    let offline = true;
    if (type === 'MLK') offline = false;
    else if (diff < threshold) offline = false;

    return (
        <span className={classnames({'ui-rf-tab-long-offline': offline})}>
            {prettyTimeInterval(Math.max(0, diff), {short: true})}
            {' назад'}
        </span>
    );
};

const RFDeviceCard = ({
    bat,
    bat_v,
    color,
    dbm,
    hum,
    motion_until,
    name,
    serial,
    server_time,
    slot,
    temp,
    time,
    type,
    water,
}) => {
    const title = rf_title_by_type(type, slot);

    const current_server_time = currentServerUnixTime(server_time);

    return (
        <div
            className={classnames('ui-card', 'ui-rf-tab-card')}
            style={{borderColor: color, borderRadius: 12}}
        >
            <div className="ui-rf-tab-indicators">
                <RFSignalIndicator dbm={dbm} />
                {' '}
                <RFBatteryIndicator bat={bat} bat_v={bat_v} />
            </div>

            <h1 title={title}>{name ?? title}</h1>

            <div className="ui-rf-tab-misc">
                <div>Серийный номер: {human_serial(serial)}</div>{' '}
                {time != null && (
                    <RFLastDataTime
                        current_time={current_server_time}
                        time={time}
                        type={type}
                    />
                )}
            </div>

            <div className="ui-rf-tab-main">
                {temp != null && show_temp_for_rf_type(type) && (
                    <RFTempIndicator temp={temp} />
                )}
                {water != null && <RFWaterIndicator water={water} />}
                {motion_until !== undefined && (
                    <RFMotionIndicator motion={motion_until > current_server_time} />
                )}
                {hum != null && <RFHumidityIndicator hum={hum} />}
            </div>
        </div>
    );
};

const UpdateButton = ({updating, updating_successful, onClick}) => (
    <div
        className={classnames('ui-rf-tab-update', {
            'ui-rf-tab-update-updating': updating,
        })}
    >
        <span
            className={classnames('ui-rf-tab-update-label', {
                error: !updating && !updating_successful,
            })}
        >
            {updating ? 'загрузка' : updating_successful ? 'готово' : 'ошибка'}
        </span>
        <span
            className="ui-rf-tab-update-button"
            title="обновить"
            onClick={!updating ? onClick : null}
        />
    </div>
);

const RFTabContent = ({active, server_time, r_device, onUpdateClick}) => {
    let {online, rf_status, thermometers, rf_updating, rf_updating_successful} =
        r_device;
    if (rf_status == null) rf_status = {};

    const active_thermometers = LegacyDevice.active_thermometers(
        r_device?.thermometers
    );

    return (
        <TimedRenderer active={active} interval={5e3}>
            {() => (
                <div className={classnames('ui-rf-tab-content')}>
                    <div
                        className="ui-cards-container"
                        style={{maxWidth: '1024px', margin: '0 auto'}}
                    >
                        {Object.entries(rf_status ?? {})
                            .filter(([_serial, rf_props]) => !rf_props.uninitialized)
                            .map(([serial, rf_props]) => (
                                <RFDeviceCard
                                    key={serial}
                                    server_time={server_time}
                                    serial={serial}
                                    color={LegacyDevice.rf_color(thermometers, serial)}
                                    {...rf_props}
                                    name={LegacyDevice.rf_name(
                                        active_thermometers,
                                        rf_status,
                                        serial
                                    )}
                                />
                            ))}

                        {online && (
                            <div
                                style={{
                                    flexBasis: '720px',
                                    textAlign: 'right',
                                    margin: '8px',
                                    marginTop: 0,
                                }}
                            >
                                <UpdateButton
                                    updating_successful={rf_updating_successful}
                                    updating={rf_updating}
                                    onClick={onUpdateClick}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </TimedRenderer>
    );
};

export const RFTab = connect(
    (state) => ({
        server_time: state.server_time,
        r_device: selectSelectedDevice(state),
    }),

    (dispatch, ownProps) => ({
        onUpdateClick: () => dispatch(reloadDeviceRF(ownProps.r_device.id)),
    })
)(RFTabContent);

RFTab.should_appear_depends_on = ['capabilities', 'access', 'rf_status'];
RFTab.should_appear = (device, account, r_device) =>
    LegacyDevice.has_capability(r_device?.capabilities, Capability.has_external_rf) &&
    Object.keys(r_device.rf_status ?? {}).length > 0;

export const rfTabDefinition = {
    key: 'rf',
    title: 'Радио',
    iconclass: 'ui-rf-tab-icon',
    should_appear_depends_on: RFTab.should_appear_depends_on,
    should_appear: RFTab.should_appear,
    render: RFTab,
};
